import React, { useState, useEffect, useRef, useCallback } from "react";
import { createReading, updateReading, searchVocabulary, searchPhrases } from "./adminReadingService";
import ImagePickerModal from "../../media_images/ImagePickerModal";
import RichTextEditor from "../../components/rich_text_editor/RichTextEditor";
import TopicLinksPanel from "../../content/TopicLinksPanel";
import "../../styles/content.css";
import "./readingForm.css";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${BASE_ROOT}${pub}`;
  console.log("[ReadingForm] img raw:", url, "→ resolved:", src);
  return src;
};

const defaultForm = {
  title: "",
  content: "",
  difficultyId: "",
  categoryId: "",
  sortOrder: "",
  youtubeUrl: "",
  warmupQuestions: [],
  trueFalseStatements: [],
  discussionQuestions: [],
};

const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([-\w]{11})/);
  return m ? m[1] : null;
};



// ── Searchable Multi-Select ──────────────────────────────────────────────────
const SearchMultiSelect = ({ label, selectedItems, onAdd, onRemove, searchFn, placeholder }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleSearch = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      const res = await searchFn(q);
      if (res.status === 200 && Array.isArray(res.data)) { setResults(res.data); setOpen(true); }
    }, 300);
  }, [searchFn]);

  useEffect(() => { handleSearch(query); }, [query, handleSearch]);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedIds = new Set(selectedItems.map((i) => i.id));

  return (
    <div className="form-group" ref={wrapperRef}>
      <label className="rf-label">{label}</label>
      <div className="rf-tags">
        {selectedItems.map((item) => (
          <span key={item.id} className="rf-tag">
            {item.label}
            <button type="button" onClick={() => onRemove(item.id)} className="rf-tag-remove">✕</button>
          </span>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} className="rf-input" />
        {open && results.length > 0 && (
          <div className="rf-dropdown">
            {results.map((r) => (
              <div key={r.id} onClick={() => { if (!selectedIds.has(r.id)) onAdd(r); setQuery(""); setOpen(false); }}
                className={`rf-dropdown-item ${selectedIds.has(r.id) ? "rf-dropdown-item--selected" : ""}`}>
                <div className="rf-dropdown-label">{r.label}</div>
                {r.subLabel && <div className="rf-dropdown-sublabel">{r.subLabel}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Section Images Panel ─────────────────────────────────────────────────────
const SectionImages = ({ images, onChange }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const dragIdx = useRef(null);

  const handleSelect = (url) => {
    // url is like "/api/media-images/42/file" or "/api/public/media-images/42/file"
    const match = url.match(/\/api(?:\/public)?\/media-images\/(\d+)\/file/);
    const mediaImageId = match ? Number(match[1]) : null;
    if (!mediaImageId) return;
    const newImg = { mediaImageId, url, sortOrder: images.length + 1, altText: "" };
    onChange([...images, newImg]);
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const arr = [...images];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange(arr.map((img, i) => ({ ...img, sortOrder: i + 1 })));
  };

  const updateField = (idx, key, val) => {
    const arr = [...images];
    arr[idx] = { ...arr[idx], [key]: val };
    onChange(arr);
  };

  // Drag handlers
  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const arr = [...images];
    const dragged = arr.splice(dragIdx.current, 1)[0];
    arr.splice(idx, 0, dragged);
    dragIdx.current = idx;
    onChange(arr.map((img, i) => ({ ...img, sortOrder: i + 1 })));
  };
  const onDragEnd = () => { dragIdx.current = null; };

  return (
    <div className="rf-section">
      <div className="rf-section-header">
        <span className="rf-label">Section Images</span>
        <button type="button" className="rf-add-btn" onClick={() => setPickerOpen(true)}>+ Add Image</button>
      </div>
      {images.length === 0 && <p className="rf-empty">No images added. Images appear between paragraphs based on sort order.</p>}
      <div className="rf-images-list">
        {images.map((img, idx) => (
          <div key={idx} className="rf-image-item" draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDragEnd={onDragEnd}>
            <span className="rf-drag-handle" title="Drag to reorder">⠿</span>
            <img src={toPublicImgSrc(img.url)} alt={img.altText || ""} className="rf-image-thumb" />
            <div className="rf-image-meta">
              <input
                placeholder="Alt text"
                value={img.altText}
                onChange={(e) => updateField(idx, "altText", e.target.value)}
                className="rf-input rf-input--sm"
              />
              <div className="rf-image-order">
                <label className="rf-label-inline">After para:</label>
                <input
                  type="number" min={0}
                  value={img.sortOrder}
                  onChange={(e) => updateField(idx, "sortOrder", Number(e.target.value))}
                  className="rf-input rf-input--xs"
                />
              </div>
            </div>
            <div className="rf-image-actions">
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="rf-icon-btn" title="Move up">↑</button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === images.length - 1} className="rf-icon-btn" title="Move down">↓</button>
              <button type="button" onClick={() => remove(idx)} className="rf-icon-btn rf-icon-btn--danger" title="Remove">✕</button>
            </div>
          </div>
        ))}
      </div>
      <ImagePickerModal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={handleSelect} defaultImageUse="GENERAL" />
    </div>
  );
};

// ── Quick Entry Section (Enter-to-add + bulk paste) ─────────────────────────
// For warmupQuestions / discussionQuestions: items = [{ question, sortOrder }]
// For trueFalseStatements:                   items = [{ statement, correctAnswer, sortOrder }]
// isTrueFalse flag switches the field name and adds the T/F toggle
const QuickEntrySection = ({ label, hint, items, onChange, isTrueFalse = false }) => {
  const [draft, setDraft] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const textKey = isTrueFalse ? "statement" : "question";

  const addLine = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (isTrueFalse) {
      const parts = trimmed.split("|");
      const statement = parts[0].trim();
      const answer = parts[1]?.trim().toLowerCase();
      const correctAnswer = answer === "false" ? false : true;
      onChange([...items, { statement, correctAnswer, sortOrder: items.length + 1 }]);
    } else {
      onChange([...items, { question: trimmed, sortOrder: items.length + 1 }]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addLine(draft);
      setDraft("");
    }
  };

  const handleBulkAdd = () => {
    const lines = bulkText.split("\n").filter((l) => l.trim());
    const base = items.length;
    const newItems = lines.map((line, i) => {
      if (isTrueFalse) {
        const parts = line.split("|");
        const statement = parts[0].trim();
        const answer = parts[1]?.trim().toLowerCase();
        const correctAnswer = answer === "false" ? false : true;
        return { statement, correctAnswer, sortOrder: base + i + 1 };
      }
      return { question: line.trim(), sortOrder: base + i + 1 };
    });
    onChange([...items, ...newItems]);
    setBulkText("");
    setBulkOpen(false);
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, sortOrder: i + 1 })));

  const updateField = (idx, key, val) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], [key]: val };
    onChange(arr);
  };

  return (
    <div className="rf-section">
      <div className="rf-section-header">
        <span className="rf-label">{label}</span>
        <button type="button" className="rf-add-btn" onClick={() => setBulkOpen((v) => !v)}>
          {bulkOpen ? "✕ Close Bulk" : "⊞ Bulk Paste"}
        </button>
      </div>

      {/* Enter-to-add input */}
      <div className="rf-quick-entry">
        <input
          className="rf-input"
          placeholder={hint}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="button" className="rf-add-btn" onClick={() => { addLine(draft); setDraft(""); }}>+ Add</button>
      </div>
      <span className="rf-char-count">Press Enter or click Add</span>

      {/* Bulk paste panel */}
      {bulkOpen && (
        <div className="rf-bulk-panel">
          <p className="rf-bulk-hint">
            {isTrueFalse
              ? <>One per line. Format: <code>Statement text | true</code> or <code>Statement text | false</code> (default: true)</>  
              : "One question per line. Paste from doc or sheet."}
          </p>
          <textarea
            className="rf-textarea rf-textarea--bulk"
            rows={5}
            placeholder={isTrueFalse
              ? "Remote work is more productive. | true\nAll meetings must be in-person. | false"
              : "What do you think about remote work?\nHave you ever worked from home?"}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="rf-btn rf-btn--primary" onClick={handleBulkAdd} disabled={!bulkText.trim()}>Add All</button>
            <button type="button" className="rf-btn rf-btn--secondary" onClick={() => { setBulkText(""); setBulkOpen(false); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Item list */}
      {items.length > 0 && (
        <div className="rf-qe-list">
          {items.map((item, i) => (
            <div key={i} className="rf-qe-item">
              <span className="rf-qe-num">{i + 1}.</span>
              <input
                className="rf-input rf-input--grow"
                value={item[textKey]}
                onChange={(e) => updateField(i, textKey, e.target.value)}
              />
              {isTrueFalse && (
                <button
                  type="button"
                  className={`rf-tf-badge ${item.correctAnswer ? "rf-tf-badge--true" : "rf-tf-badge--false"}`}
                  onClick={() => updateField(i, "correctAnswer", !item.correctAnswer)}
                  title="Click to toggle True / False"
                >
                  {item.correctAnswer ? "True" : "False"}
                </button>
              )}
              <button type="button" className="rf-icon-btn rf-icon-btn--danger" onClick={() => remove(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Form ────────────────────────────────────────────────────────────────
const ReadingForm = ({ passage, categories, difficulties, onSaved, onCancel }) => {
  const isEdit = !!passage;
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [selectedVocab, setSelectedVocab] = useState([]);
  const [selectedPhrases, setSelectedPhrases] = useState([]);
  const [sectionImages, setSectionImages] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (passage) {
      setForm({
        title: passage.title || "",
        content: passage.content || "",
        difficultyId: passage.difficultyId ?? (typeof passage.difficulty === "object" ? passage.difficulty?.id : "") ?? "",
        categoryId: passage.categoryId ?? (typeof passage.category === "object" ? passage.category?.id : "") ?? "",
        sortOrder: passage.sortOrder ?? "",
        youtubeUrl: passage.youtubeUrl || "",
        warmupQuestions: passage.warmupQuestions?.map((q) => ({ question: q.question, sortOrder: q.sortOrder })) || [],
        trueFalseStatements: passage.trueFalseStatements?.map((s) => ({ statement: s.statement, correctAnswer: s.correctAnswer, sortOrder: s.sortOrder })) || [],
        discussionQuestions: passage.discussionQuestions?.map((q) => ({ question: q.question, sortOrder: q.sortOrder })) || [],
      });
      setSelectedVocab(passage.vocabularies?.map((v) => ({ id: v.id, label: v.word, subLabel: v.definition })) || []);
      setSelectedPhrases(passage.phrases?.map((p) => ({ id: p.id, label: p.title, subLabel: p.meaning })) || []);
      setSectionImages(passage.images?.map((img) => ({ mediaImageId: img.mediaImageId, url: img.url, sortOrder: img.sortOrder, altText: img.altText || "" })) || []);
    }
  }, [passage]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim() || !form.content.trim()) { setError("Title and Content are required."); return; }
    const payload = {
      title: form.title,
      content: form.content,
      difficultyId: form.difficultyId ? Number(form.difficultyId) : null,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      youtubeUrl: form.youtubeUrl || null,
      warmupQuestions: form.warmupQuestions,
      trueFalseStatements: form.trueFalseStatements,
      discussionQuestions: form.discussionQuestions,
      vocabularyIds: selectedVocab.map((v) => v.id),
      phraseIds: selectedPhrases.map((p) => p.id),
      images: sectionImages.map(({ mediaImageId, sortOrder, altText }) => ({ mediaImageId, sortOrder, altText })),
    };
    setLoading(true);
    const res = isEdit ? await updateReading(passage.id, payload) : await createReading(payload);
    setLoading(false);
    if (res.status === 200 || res.status === 201) { onSaved(); }
    else { setError(res.message || "Operation failed. Please try again."); }
  };

  return (
    <div className="rf-page">
      {/* ── Top Bar ── */}
      <div className="rf-topbar">
        <button type="button" className="rf-back-btn" onClick={onCancel}>← Back to List</button>
        <h2 className="rf-title">{isEdit ? "Edit" : "Create"} Reading Passage</h2>
        <div className="rf-topbar-actions">
          <button type="button" onClick={onCancel} className="rf-btn rf-btn--secondary" disabled={loading}>Cancel</button>
          <button type="button" onClick={handleSubmit} className="rf-btn rf-btn--primary" disabled={loading}>
            {loading ? "Saving…" : isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>

      {error && <div className="rf-error">{error}</div>}

      {/* ── Two-column body ── */}
      <div className="rf-body">
        {/* LEFT */}
        <div className="rf-col rf-col--left">
          <div className="rf-card">
            <div className="form-group">
              <label className="rf-label">Title <span className="rf-required">*</span></label>
              <input name="title" placeholder="e.g. The Future of Remote Work" value={form.title} onChange={handleChange} className="rf-input" />
            </div>

            {/* Content with Rich Text Editor */}
            <div className="form-group">
              <label className="rf-label">Content <span className="rf-required">*</span></label>
              <RichTextEditor
                value={form.content}
                onChange={(val) => setForm((f) => ({ ...f, content: val }))}
                placeholder="Type or paste content here…"
              />
            </div>
          </div>

          {/* Warm-up Questions */}
          <div className="rf-card">
            <QuickEntrySection
              label="Warm-up Questions"
              hint="Type a question and press Enter…"
              items={form.warmupQuestions}
              onChange={(val) => setForm((f) => ({ ...f, warmupQuestions: val }))}
            />
          </div>

          {/* True/False */}
          <div className="rf-card">
            <QuickEntrySection
              label="True / False Statements"
              hint="Type a statement and press Enter… (default: True)"
              items={form.trueFalseStatements}
              onChange={(val) => setForm((f) => ({ ...f, trueFalseStatements: val }))}
              isTrueFalse
            />
          </div>

          {/* Discussion Questions */}
          <div className="rf-card">
            <QuickEntrySection
              label="Discussion Questions"
              hint="Type a question and press Enter…"
              items={form.discussionQuestions}
              onChange={(val) => setForm((f) => ({ ...f, discussionQuestions: val }))}
            />
          </div>
        </div>

        {/* RIGHT */}
        <div className="rf-col rf-col--right">
          {/* Meta */}
          <div className="rf-card">
            <div className="form-group">
              <label className="rf-label">Difficulty</label>
              <select name="difficultyId" value={form.difficultyId} onChange={handleChange} className="rf-select">
                <option value="">— Select —</option>
                {difficulties.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="rf-label">Category</label>
              <select name="categoryId" value={form.categoryId} onChange={handleChange} className="rf-select">
                <option value="">— Select —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="rf-label">Sort Order</label>
              <input name="sortOrder" type="number" placeholder="e.g. 1" value={form.sortOrder} onChange={handleChange} className="rf-input" />
            </div>
            <div className="form-group">
              <label className="rf-label">YouTube / Vimeo URL</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input name="youtubeUrl" type="url" placeholder="https://www.youtube.com/watch?v=…"
                  value={form.youtubeUrl} onChange={handleChange} className="rf-input" style={{ flex: 1 }} />
                {form.youtubeUrl && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, youtubeUrl: "" }))} className="rf-icon-btn rf-icon-btn--danger">✕</button>
                )}
              </div>
              {extractYoutubeId(form.youtubeUrl) && (
                <img src={`https://img.youtube.com/vi/${extractYoutubeId(form.youtubeUrl)}/hqdefault.jpg`}
                  alt="thumbnail" className="rf-yt-thumb"
                  onError={(e) => { e.target.style.display = "none"; }} />
              )}
            </div>
          </div>

          {/* Section Images */}
          <div className="rf-card">
            <SectionImages images={sectionImages} onChange={setSectionImages} />
          </div>

          {/* Vocabulary */}
          <div className="rf-card">
            <SearchMultiSelect
              label="Vocabulary"
              selectedItems={selectedVocab}
              onAdd={(item) => setSelectedVocab((prev) => [...prev, item])}
              onRemove={(id) => setSelectedVocab((prev) => prev.filter((v) => v.id !== id))}
              searchFn={searchVocabulary}
              placeholder="Search vocabulary words…"
            />
          </div>

          {/* Phrases */}
          <div className="rf-card">
            <SearchMultiSelect
              label="Phrases"
              selectedItems={selectedPhrases}
              onAdd={(item) => setSelectedPhrases((prev) => [...prev, item])}
              onRemove={(id) => setSelectedPhrases((prev) => prev.filter((p) => p.id !== id))}
              searchFn={searchPhrases}
              placeholder="Search phrases…"
            />
          </div>

          {/* Linked Content / Practice / Quiz */}
          {isEdit && (
            <div className="rf-card">
              <TopicLinksPanel topicType="READING_PASSAGE" topicId={passage.id} />
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ReadingForm;
